import React from 'react';
import { Navigate, useLocation, matchPath } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth, UserRole } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  requiredPermission?: string;
}

// Map routes to required permissions
const routePermissionMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/student-registration': 'studentRegistration',
  '/users': 'users',
  '/job-seekers': 'jobSeekers',
  '/user-analytics': 'userAnalytics',
  '/jobs': 'jobs',
  '/applications': 'applications',
  '/company-profiles': 'companyProfiles',
  '/courses': 'courses',
  '/psychometric-tests': 'psychometricTests',
  '/smart-tests': 'smartTests',
  '/ai-interviews': 'aiInterviews',
  '/past-papers': 'pastPapers',
  '/certificates': 'certificates',
  '/videos': 'videos',
  '/blog-posts': 'blogPosts',
  '/system-analytics': 'systemAnalytics',
  '/system-settings': 'systemSettings',
  '/system-health': 'systemHealth',
  '/email-events': 'emailEvents',
  '/teacher-attendance': 'teacherAttendance',
  '/performance': 'performance',
  '/usage-stats': 'usageStats',
  '/super-admin-management': 'superAdminManagement',
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles, requiredPermission }) => {
  const { user, isAuthenticated, isLoading, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (roles && roles.length > 0) {
    const hasRequiredRole = roles.includes(user.role as UserRole);

    if (!hasRequiredRole) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required roles: {roles.join(', ')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your role: {user.role}
          </Typography>
        </Box>
      );
    }
  }

  // Check permission-based access for super admins
  // Skip permission check for primary super admin who has all permissions
  if (user.role === UserRole.SUPER_ADMIN && !user.isPrimarySuperAdmin) {
    // Find required permission for current route
    const currentPath = location.pathname;
    let requiredPerm = requiredPermission;

    // If no explicit permission provided, look up from route map
    if (!requiredPerm) {
      for (const [route, perm] of Object.entries(routePermissionMap)) {
        if (matchPath(route, currentPath)) {
          requiredPerm = perm;
          break;
        }
      }
    }

    // Check if user has the required permission
    if (requiredPerm && !hasPermission(requiredPerm as any)) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contact the primary super admin if you need access to this feature.
          </Typography>
        </Box>
      );
    }
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default ProtectedRoute;